<script setup lang="ts">
import { getRelicName, relicCollection } from '@/core/relic'
import { useUserStore } from '@/stores/modules/user'
import type { RelicItemType, RelicInfoType } from '@/types/relic'
import RelicInfo from '@/views/item/components/RelicInfo.vue'
import { relicIcon } from '@/views/item/data'
import { ref } from 'vue'
import { socketEmitAsync } from '@/utils/web-socket'
import { relicNeedStarMap } from '../data'
import { computed } from 'vue'

const userStore = useUserStore()

const allRelicList: RelicInfoType[] = Object.entries(relicCollection).map(([id, item]) => {
  return {
    ...item,
    id,
    relicId: Number(id),
    count: 50,
    isLock: false,
  }
})

const filterElementType = ref<string[]>([])
const filterRelicName = ref('')
function handleAddFilterElement(element: string) {
  const index = filterElementType.value.findIndex((item) => item === element)
  if (index !== -1) return filterElementType.value.splice(index, 1)
  filterElementType.value.push(element)
}
function handleResetRelicFilter() {
  filterElementType.value = []
  filterRelicName.value = ''
}

const relicItemList = computed(() =>
  allRelicList.filter((item) => {
    const { name, element } = relicCollection[item.relicId]
    return (filterElementType.value.length ? filterElementType.value.includes(element) : true) && name.includes(filterRelicName.value)
  }),
)

const relicInfo = ref<RelicItemType>()
function showDetail(item: RelicItemType) {
  relicInfo.value = item
}
const needRelicStar = computed(() => {
  if (!relicInfo.value) return 0
  return relicNeedStarMap[relicInfo.value.quality] * (relicCollection[relicInfo.value.relicId].changeAtkElement ? 5 : 1)
})
const cantSynthesis = computed(() => {
  return relicCollection[relicInfo.value?.relicId || 0]?.changeAtkElement && relicInfo.value?.quality !== 'race'
})
function handleSynthesis() {
  if (userStore.consumables.relicStar < 50) return ElMessage.warning('圣物碎星不足')
  if (!relicInfo.value) return

  const { relicId, quality } = relicInfo.value
  ElMessageBox.confirm(
    `<p class="affix">确定合成【<span class="${quality}">${getRelicName(relicId, 0)}</span>】吗？（本次合成消耗<span class="relic">${
      needRelicStar.value
    }</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const res = await socketEmitAsync<RelicItemType>({ type: 'synthesisRelic', data: { relicId: relicInfo.value?.relicId } })
      userStore.getRelicToBag([res])
      userStore.consumables.relicStar -= needRelicStar.value
      ElMessage.success('合成成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="relic-collection">
    <div class="left border-wrap">
      <h4>圣物列表</h4>
      <div class="action">
        <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
          <template #reference>
            <el-button size="small" type="primary" plain>筛选</el-button>
          </template>
          <p>元素</p>
          <el-button
            size="small"
            type="primary"
            plain
            class="item"
            v-for="(val, key) of relicIcon"
            :key="key"
            @click="handleAddFilterElement(key)"
            :style="{ borderColor: filterElementType?.includes(key) ? 'red' : '' }"
          >
            <span>{{ val }}</span>
          </el-button>
          <p>搜索名称</p>
          <el-input size="small" v-model="filterRelicName"></el-input>
          <br />
          <br />
          <el-button size="small" type="success" plain @click="handleResetRelicFilter">重置</el-button>
        </el-popover>
      </div>
      <div class="item-btn-wrap btn-wrap">
        <CommonBtn
          v-for="item of relicItemList"
          :key="item.id"
          size="small"
          @done="showDetail(item)"
          :style="{ position: 'relative', borderColor: relicInfo?.id === item.id ? 'red' : '' }"
        >
          <span :class="item.quality"> {{ getRelicName(item.relicId, item.count) }}</span>
        </CommonBtn>
      </div>
    </div>
    <div class="right">
      <div class="action border-wrap">
        <h4>操作</h4>

        <p style="font-size: 12px; margin: 5px 0">持有圣物碎星： {{ userStore.consumables.relicStar }}</p>
        <el-button type="primary" plain size="small" :disabled="cantSynthesis || !needRelicStar" @click="handleSynthesis">{{
          cantSynthesis ? '不支持合成' : `合成${needRelicStar ? `（${needRelicStar} 圣物碎星）` : ''}`
        }}</el-button>
      </div>
      <div class="relic-info border-wrap">
        <h4>圣物信息</h4>

        <template v-if="relicInfo">
          <RelicInfo :relic-info="relicInfo" :show-action="false" :show-grow="true" />
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.relic-collection {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  flex: 1;
  justify-content: space-between;
  h4 {
    margin: 10px 0;
    display: flex;
    justify-content: center;
    position: relative;
    .help-info {
      position: absolute;
      margin-left: 10px;
      right: 30%;
    }
  }
  .border-wrap {
    padding: 0 5px;
  }
  .left {
    width: calc(50% - 5px);
    margin-bottom: 10px;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-flow: column;
    .action {
      padding: 0 5px 5px;
    }
    .item-btn-wrap {
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .btn-wrap > div {
      margin-bottom: 10px;
    }
  }
  .right {
    width: calc(50% - 5px);
    display: flex;
    flex-flow: column nowrap;
    .action {
      margin-bottom: 10px;
      padding-bottom: 10px;
      .el-button {
        width: 100%;
      }
    }
    .relic-info {
      height: 100%;
    }
  }
}
</style>
